import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { Bundle, SubscriptionStatus } from '@prisma/client';
import { Button, Card, Layout, Space, Switch, Table, Tag, Typography } from 'antd';

import { Role } from '@app/domain-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Auth, FeatureFlags } from '@app/web-components';

import { SubscriptionAccessStatusRenderer } from '../../admin/reports/access-status';
import { usePatchBundle } from '../../bundles';
import { useSubscriptionStatusTag } from '../../subscriptions/atoms';
import { RetryButton } from '../../subscriptions/modals/detail';
import { useGetMyAccessRequests, useGetMyBundles, useGetMySubscriptions } from '../query';

const BundleSwitch = ({ bundle, field }: { bundle: Bundle; field: keyof Bundle }) => {
  const patch = usePatchBundle(bundle.id);
  return (
    <Switch
      size="small"
      checkedChildren="ON"
      unCheckedChildren="OFF"
      value={bundle[field] as boolean}
      onClick={() => patch.mutate({ [field]: !bundle[field] })}
    />
  );
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = Auth.useUserAuth();
  const { data: accessRequests, isLoading: isAccessRequestsLoading, refetch } = useGetMyAccessRequests();
  const { data: bundles, isLoading: isBundlesLoading } = useGetMyBundles();
  const { data: subscriptions, isLoading: isSubscriptionsLoading } = useGetMySubscriptions();
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();
  const { getProps: getSubscriptionStatusTagProps } = useSubscriptionStatusTag();

  return (
    <Layout.Content
      style={{
        minHeight: 280,
        backgroundImage: `url(../assets/images/gettyimages-869509756-1920x1080.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'cover',
        overflow: 'auto',
      }}
    >
      {isFeatureEnabled('home_dashboard') ? (
        <div
          style={{
            padding: 24,
            margin: 'auto',
            maxWidth: 1200,
            minHeight: 0,
          }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
              <Table
                caption={<strong>My Access Requests</strong>}
                columns={[
                  {
                    title: 'Retry',
                    dataIndex: ['id'],
                    width: '100px',
                    render: (value) => <RetryButton subscriptionAccessId={value} onReload={() => refetch()} />,
                  },
                  {
                    title: 'Access',
                    dataIndex: ['bundleAccess', 'access', 'name'],
                  },
                  {
                    title: 'Bundle',
                    dataIndex: ['subscription', 'bundle', 'name'],
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (value, record) => (
                      <SubscriptionAccessStatusRenderer
                        access={record.bundleAccess.access}
                        status={value}
                        user={record.subscription.user}
                      />
                    ),
                  },
                  {
                    title: 'Status Description',
                    dataIndex: 'statusDescription',
                  },
                  { title: 'Last Updated On', dataIndex: 'updatedOn', render: (value) => formatIsoDate(value) },
                ]}
                dataSource={accessRequests?.data ?? []}
                loading={isAccessRequestsLoading}
                size="small"
              />
              <Typography.Paragraph>
                To view details on pending AGS requests, please visit{' '}
                <a href="https://ags.intel.com/identityiq/identityRequest/identityRequest.jsf#/requests" target="blank">
                  AGS's "Track My Access Requests"
                </a>{' '}
                page.
              </Typography.Paragraph>
            </Card>

            <Card>
              <Table
                caption={<strong>My Bundle Subscriptions</strong>}
                columns={[
                  {
                    title: 'Bundle',
                    dataIndex: ['bundle', 'name'],
                    render: (value, record) => (
                      <Button type="link" onClick={() => navigate(`/subscriptions/${record.id}`)}>
                        {value}
                      </Button>
                    ),
                  },
                  {
                    title: 'Status',
                    dataIndex: ['status', 'status'],
                    render: (status: SubscriptionStatus) => <Tag {...getSubscriptionStatusTagProps(status)} />,
                  },
                ]}
                dataSource={subscriptions?.data ?? []}
                loading={isSubscriptionsLoading}
                size="small"
              />
              <Button icon={<PlusOutlined />} onClick={() => navigate('/subscriptions/create')}>
                Create Subscription
              </Button>
            </Card>

            {user?.roles[Role.BUNDLE_ADMIN] && (
              <Card>
                <Table
                  caption={<strong>My Bundles</strong>}
                  columns={[
                    {
                      title: 'Bundle',
                      dataIndex: ['name'],
                      render: (value, record) => (
                        <Button type="link" onClick={() => navigate(`/bundles/${record.id}/manage`)}>
                          {value}
                        </Button>
                      ),
                    },
                    {
                      title: 'Justification',
                      render: (_, record) => <BundleSwitch bundle={record as Bundle} field="justificationRequired" />,
                    },
                    {
                      title: 'Eager Pull',
                      render: (_, record) => <BundleSwitch bundle={record as Bundle} field="pullSubscriptionEnabled" />,
                    },
                    {
                      title: 'Removal Notifications',
                      render: (_, record) => <BundleSwitch bundle={record as Bundle} field="autoRemovalEnabled" />,
                    },
                    {
                      title: 'Active',
                      render: (_, record) => <BundleSwitch bundle={record as Bundle} field="isActive" />,
                    },
                  ]}
                  dataSource={bundles?.data ?? []}
                  loading={isBundlesLoading}
                  size="small"
                />
                <Button icon={<PlusOutlined />} onClick={() => navigate('/bundles/create')}>
                  Create Bundle
                </Button>
              </Card>
            )}
          </Space>
        </div>
      ) : (
        <div
          style={{
            margin: 'auto',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
            borderRadius: 10,
            paddingTop: 20,
            paddingRight: 100,
            paddingBottom: 30,
            paddingLeft: 50,
          }}
        >
          <Space direction="vertical">
            <div
              style={{
                fontSize: 90,
                lineHeight: 1,
              }}
            >
              SAAF
            </div>
            <div style={{ marginLeft: 10 }}>
              <Typography style={{ fontSize: 18, color: 'white' }}>
                <b>S</b>treamlined <b>A</b>ccess <b>A</b>pproval <b>F</b>low
              </Typography>
              <Space style={{ marginTop: 50 }}>
                <Button onClick={() => navigate('subscriptions/create')}>Subscribe</Button>
                {user?.roles?.[Role.BUNDLE_ADMIN] ? (
                  <Button onClick={() => navigate('bundles/create')}>Create Bundle</Button>
                ) : undefined}
                <a href="https://goto.intel.com/saafwiki" target="_blank" style={{ color: 'white' }} rel="noreferrer">
                  Wiki
                </a>
              </Space>
            </div>
          </Space>
        </div>
      )}
    </Layout.Content>
  );
};
